import { useState, useEffect } from "react";
import { X, Mail, User, Gift, ArrowRight } from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ExitIntentPopup() {
  const [show, setShow] = useState(false);
  const [formData, setFormData] = useState({ firstName: "", email: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (localStorage.getItem("mbsh_popup_dismissed")) return;
    // Never interrupt a buyer / post-conversion pages with a free lead-magnet popup.
    const suppressedRoutes = ["/book-success", "/workbook-success", "/sample-success", "/thank-you"];
    if (suppressedRoutes.includes(window.location.pathname)) return;

    let triggered = false;
    const delay = setTimeout(() => {
      const handleMouseLeave = (e) => {
        if (e.clientY <= 5 && !triggered) {
          triggered = true;
          setShow(true);
          document.removeEventListener("mouseout", handleMouseLeave);
        }
      };
      document.addEventListener("mouseout", handleMouseLeave);
      return () => document.removeEventListener("mouseout", handleMouseLeave);
    }, 5000);

    // Also trigger after 45 seconds on page as fallback for mobile / no-exit users
    const mobileTimer = setTimeout(() => {
      if (!triggered && !localStorage.getItem("mbsh_popup_dismissed")) {
        triggered = true;
        setShow(true);
      }
    }, 45000);

    return () => {
      clearTimeout(delay);
      clearTimeout(mobileTimer);
    };
  }, []);

  const dismiss = () => {
    setShow(false);
    localStorage.setItem("mbsh_popup_dismissed", "true");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/subscribe`, formData);
      setSubmitted(true);
      localStorage.setItem("mbsh_popup_dismissed", "true");
      setTimeout(() => {
        setShow(false);
        navigate("/thank-you");
      }, 1500);
    } catch {
      setSubmitted(true);
      localStorage.setItem("mbsh_popup_dismissed", "true");
      setTimeout(() => {
        setShow(false);
        navigate("/thank-you");
      }, 1500);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      data-testid="exit-intent-popup"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={dismiss} />

      {/* Modal */}
      <div className="relative bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-in">
        <style>{`
          .animate-in { animation: popIn 0.35s cubic-bezier(0.16, 1, 0.3, 1); }
          @keyframes popIn { from { opacity: 0; transform: scale(0.9) translateY(20px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        `}</style>

        <button
          onClick={dismiss}
          className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-black/5 hover:bg-black/10 flex items-center justify-center transition-colors"
          data-testid="popup-close-btn"
        >
          <X className="w-4 h-4 text-gray-500" />
        </button>

        {/* Top Banner */}
        <div className="bg-[#042F2E] px-6 py-5 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Gift className="w-5 h-5 text-[#5EEAD4]" />
            <span className="text-[#5EEAD4] text-xs font-bold uppercase tracking-widest">Before You Go</span>
          </div>
          <h2 className="font-serif text-2xl text-white">
            Wait — This Is Free
          </h2>
        </div>

        <div className="px-6 py-6">
          {!submitted ? (
            <>
              <p className="text-[#068fa2] text-center text-sm leading-relaxed mb-2">
                Get a <strong className="text-[#25a1c2]">FREE printable 365-day "I GET TO" affirmation calendar</strong> plus
                weekly mindset shifts from Temple — delivered to your inbox for an entire year.
              </p>
              <p className="text-[#475569] text-center text-xs mb-5">
                No cost. No spam. Unsubscribe anytime.
              </p>

              <form onSubmit={handleSubmit} className="space-y-3" data-testid="popup-signup-form">
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#25a1c2]" />
                  <input
                    type="text"
                    placeholder="First Name"
                    required
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    data-testid="popup-firstname"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#CCFBF1] bg-[#efeae7] text-sm focus:outline-none focus:ring-2 focus:ring-[#25a1c2]"
                  />
                </div>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#25a1c2]" />
                  <input
                    type="email"
                    placeholder="Email Address"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="popup-email"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#CCFBF1] bg-[#efeae7] text-sm focus:outline-none focus:ring-2 focus:ring-[#25a1c2]"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="popup-submit-btn"
                  className="w-full bg-[#25a1c2] hover:bg-[#068fa2] text-white rounded-xl py-3.5 font-medium text-sm transition-all hover:-translate-y-0.5 shadow-md disabled:opacity-50"
                >
                  {isSubmitting ? "Joining..." : "Get My Free Calendar + Start the Journey"}
                </button>
              </form>

              <button
                onClick={dismiss}
                className="w-full text-center text-xs text-gray-400 hover:text-gray-500 mt-3 transition-colors"
                data-testid="popup-no-thanks"
              >
                No thanks, I'll pass on the free gift
              </button>
            </>
          ) : (
            <div className="text-center py-4" data-testid="popup-success">
              <div className="w-14 h-14 bg-[#F0FDF4] rounded-full flex items-center justify-center mx-auto mb-3">
                <ArrowRight className="w-6 h-6 text-[#25a1c2]" />
              </div>
              <h3 className="font-serif text-xl text-[#042F2E] mb-2">You're In!</h3>
              <p className="text-[#475569] text-sm">Redirecting to your free calendar...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
